import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  FolderKanban,
  Users,
  FileText,
  FilePlus,
  Calculator,
  Settings,
  FileSearch,
  Target,
  Landmark,
  Trophy,
  BookOpen,
  CalendarDays,
  Radar,
  Globe,
  Plug,
  TrendingUp,
  Award,
  Swords,
  Key,
  Palette,
  Menu,
  X,
  Moon,
  Sun,
  HelpCircle,
  CreditCard,
  Brain,
  Building2,
  Shield,
  Activity,
  Sparkles,
  ListChecks,
  Calendar as CalendarIcon,
  LogOut,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import TenderWrightLogo from './TenderWrightLogo';
import NotificationBell from './NotificationBell';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// F102 (NAL pilot UX 2026-05-18): every nav item now carries a
// short `description` shown as the hover title. Without these, labels
// like "Intelligence", "Capability Statement", "Audit Trail" leave
// new users guessing what each page actually does. Descriptions stay
// under ~80 chars so they fit in a single browser tooltip line.
const navSections = [
  {
    label: 'DISCOVER',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard,
        description: "Today's actions, deadlines, and pipeline health at a glance." },
      { path: '/tenders', label: 'Tender Monitor', icon: Radar,
        description: 'Live tender feed from NZ GETS, Tenderlink, World Bank, ADB and other sources — ranked by fit to your firm.' },
      { path: '/rfp-parser', label: 'RFP Parser', icon: FileSearch,
        description: 'Upload an RFP PDF or DOCX — extracts requirements, deadlines, evaluation criteria, and compliance items into a structured table.' },
    ],
  },
  {
    label: 'EVALUATE',
    items: [
      { path: '/go-no-go', label: 'Go/No-Go', icon: Target,
        description: 'Score whether to bid: 10 weighted criteria (strategic fit, capability, capacity, profitability, risk) produce a Go / Conditional / No-Go verdict.' },
      { path: '/intelligence', label: 'Intelligence', icon: Brain,
        description: 'Win-rate analytics by project type, client, and team performance. Needs 3+ resolved proposals to activate.' },
      { path: '/competitors', label: 'Competitors', icon: Swords,
        description: 'Track competing firms you encounter — their strengths, weaknesses, recent wins, and when you typically face them.' },
      { path: '/clients', label: 'Clients', icon: Building2,
        description: 'Client CRM — historical proposals, win rate per client, contact details, and engagement history.' },
    ],
  },
  {
    label: 'BUILD',
    items: [
      { path: '/proposals', label: 'All Proposals', icon: FilePlus,
        description: 'Your active proposal pipeline — draft, in progress, in review, submitted, won, lost.' },
      { path: '/content-library', label: 'Content Library', icon: BookOpen,
        description: 'Reusable content blocks (methodology paragraphs, boilerplate, standard responses) that you can drop into any proposal.' },
      { path: '/templates', label: 'Templates', icon: FileText,
        description: 'Proposal templates — NZ Govt RFP, Australian Govt, MFAT Pacific, EOI, Subconsultant, etc. Defines the section structure for new proposals.' },
      { path: '/client-templates', label: 'Client Templates', icon: FileText,
        description: "Upload a client's mandated submission format (Word/PDF/Excel/PowerPoint), map its fields to your proposal content, then ship bids in the client's own format." },
      { path: '/fee-estimator', label: 'Fee Estimator', icon: Calculator,
        description: 'Build a structured fee breakdown by phase + role. Reads your rate cards and produces a fee table that flows into the proposal.' },
      { path: '/nz-govt-wizard', label: 'NZ Govt Wizard', icon: Landmark,
        description: 'Pre-bid readiness check for NZ Government RFPs — verifies compliance items, named engineers, and certifications before you commit to the bid.' },
      { path: '/capability-statement', label: 'Capability Statement', icon: FileText,
        description: 'Your firm capability document — services, accreditations, key projects. Used as boilerplate when responding to procurement-style RFPs.' },
    ],
  },
  {
    label: 'TEAM',
    items: [
      { path: '/people', label: 'People', icon: Users,
        description: 'Staff register — names, qualifications (CPEng, CMEngNZ), CVs, hourly rates, availability. The library the AI draws from to populate team sections.' },
      { path: '/projects', label: 'Projects', icon: FolderKanban,
        description: 'Past project portfolio — names, clients, sectors, disciplines, descriptions. Used as relevant-experience evidence in proposals.' },
      { path: '/resource-planner', label: 'Resource Planner', icon: CalendarDays,
        description: 'Allocate staff hours across active proposals — see who is overbooked and which weeks have capacity for new bids.' },
      { path: '/certifications', label: 'Certifications', icon: Award,
        description: 'Staff certifications register (CPEng, ISO, vaccination, drivers licence renewals) with expiry tracking for procurement compliance.' },
    ],
  },
  {
    label: 'INSIGHTS',
    items: [
      { path: '/impact', label: 'Impact Report', icon: Sparkles,
        description: 'ROI and time-saved summary for the firm — shareable artefact you can hand to leadership or a procurement reference.' },
      { path: '/analytics', label: 'Analytics', icon: BarChart3,
        description: 'Top-level performance: win rate, pipeline value, won revenue, proposal status breakdown.' },
      { path: '/advanced-analytics', label: 'Advanced Analytics', icon: TrendingUp,
        description: 'Deeper cuts: win/loss patterns by client and discipline, pipeline by stage, team utilisation, fee benchmarks, conflicts log.' },
      { path: '/lessons', label: 'Lessons', icon: BookOpen,
        description: 'Lessons learned from won and lost bids — capture win factors, loss factors, process improvements, pricing insights.' },
      { path: '/decisions', label: 'Decision Log', icon: ListChecks,
        description: 'Every business decision captured against your bids — go/no-go calls, win/loss outcomes — with the rationale you recorded at the time. For "why did we make this call?" review months later.' },
      { path: '/calendar', label: 'Pursuit Calendar', icon: CalendarIcon,
        description: 'All in-flight proposals with due dates on a month grid. Colour-coded by status. Click a cell to open the builder. Great for at-a-glance pursuit prioritisation.' },
      { path: '/audit-log', label: 'Audit Trail', icon: Shield,
        description: 'Immutable record of every proposal change, export, AI generation, and status transition — searchable and CSV-exportable for procurement audits.' },
      { path: '/achievements', label: 'Achievements', icon: Trophy,
        description: 'Team achievements and milestones — wins, streaks, badges earned across the firm.' },
    ],
  },
];

const SIDEBAR_COLLAPSE_STORAGE_KEY = 'sidebar-collapsed-sections';

// Determine which section contains the given pathname.
// Uses startsWith semantics matching isActive(), with /dashboard requiring exact match.
function findSectionForPath(pathname) {
  for (const section of navSections) {
    for (const item of section.items) {
      if (item.path === '/dashboard') {
        if (pathname === '/dashboard') return section.label;
      } else if (pathname.startsWith(item.path)) {
        return section.label;
      }
    }
  }
  return null;
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('bidforge-theme') === 'dark');
  const [orgs, setOrgs] = useState([]);
  const { currentOrgId, setCurrentOrgId } = useAuth();

  // Per-section collapsed state. Persisted in localStorage.
  // BUG-1 fix (NAL fresh-eyes 2026-05-18): default for new users is now
  // ALL SECTIONS EXPANDED. Previous default ("collapse all except active
  // section") hid Proposals, People, Projects, Templates, Analytics,
  // Audit Trail etc. on first landing — a brand-new user couldn't reach
  // those pages without realising the section headers were collapsible.
  // Existing users keep whatever they've chosen via the stored value.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    let stored = null;
    try {
      const raw = localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch (_) {
      stored = null;
    }
    const initial = {};
    navSections.forEach((section) => {
      if (stored && Object.prototype.hasOwnProperty.call(stored, section.label)) {
        initial[section.label] = !!stored[section.label];
      } else {
        // No stored value: expand by default so all pages are discoverable.
        initial[section.label] = false;
      }
    });
    return initial;
  });

  // When the route changes, auto-expand the section containing the new active
  // route (without collapsing other user-expanded sections).
  useEffect(() => {
    const activeSection = findSectionForPath(location.pathname);
    if (!activeSection) return;
    setCollapsedSections((prev) => {
      if (prev[activeSection] === false) return prev;
      return { ...prev, [activeSection]: false };
    });
  }, [location.pathname]);

  // Persist collapse state.
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, JSON.stringify(collapsedSections));
    } catch (_) {
      /* ignore quota errors */
    }
  }, [collapsedSections]);

  const toggleSection = (label) => {
    setCollapsedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? 'dark' : 'light';
    localStorage.setItem('bidforge-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    api.get('/api/onboarding/organizations')
      .then(res => {
        if (res.data?.length > 1) {
          setOrgs(res.data);
          // If no org is selected yet, default to the authenticated user's org
          if (!currentOrgId) {
            const defaultOrg = user?.organization_id || res.data[0]?.id || '';
            if (defaultOrg) setCurrentOrgId(defaultOrg);
          }
        }
      })
      .catch(() => {});
  }, [user?.organization_id]); // eslint-disable-line

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <>
    <button className="sidebar-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
      {mobileOpen ? <X size={20} /> : <Menu size={20} />}
    </button>
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-brand">
        <TenderWrightLogo size={36} />
        {!collapsed && (
          <div>
            {/* Demoted from <h1> 2026-05-10. Each authenticated page already
                renders its own page <h1>; the brand wordmark was producing
                a second <h1> per document, breaking screen-reader page
                outline (WCAG 1.3.1). The visual class is preserved so the
                CSS doesn't change. */}
            <p className="sidebar-brand-title" aria-hidden="true">TenderWright</p>
            <span>Engineering Proposals</span>
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <NotificationBell />
          <button
            className="btn btn-ghost btn-sm sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            style={{ padding: 4 }}
          >
            {collapsed ? <Menu size={16} /> : <X size={16} />}
          </button>
        </div>
      </div>

      {orgs.length > 1 && !collapsed && (
        <div style={{ padding: '0 12px 8px', borderBottom: '1px solid var(--border)' }}>
          <select
            value={currentOrgId || orgs[0]?.id || ''}
            onChange={(e) => {
              setCurrentOrgId(e.target.value);
              window.location.reload();
            }}
            style={{
              width: '100%', padding: '6px 8px', fontSize: '0.75rem', fontWeight: 600,
              borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--light)', color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
      )}

      {navSections.map((section) => {
        const isSectionCollapsed = !!collapsedSections[section.label];
        // When the whole sidebar is collapsed (icon-only mode), always show
        // every link so the user can still navigate. The per-section
        // collapse only applies in expanded sidebar mode.
        const showItems = collapsed || !isSectionCollapsed;
        return (
          <div className="sidebar-section" key={section.label}>
            {!collapsed && (
              <button
                type="button"
                className="sidebar-section-label sidebar-section-toggle"
                onClick={() => toggleSection(section.label)}
                aria-expanded={!isSectionCollapsed}
                aria-controls={`sidebar-section-${section.label}`}
              >
                <span>{section.label}</span>
                <ChevronDown
                  size={12}
                  aria-hidden="true"
                  className={`sidebar-section-chevron ${isSectionCollapsed ? 'is-collapsed' : ''}`}
                />
              </button>
            )}
            {showItems && (
              <nav className="sidebar-nav" id={`sidebar-section-${section.label}`} aria-label={section.label}>
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => navigate(item.path)}
                    aria-label={item.description ? `${item.label}: ${item.description}` : item.label}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    title={item.description || item.label}
                  >
                    <item.icon aria-hidden="true" />
                    {!collapsed && item.label}
                  </button>
                ))}
              </nav>
            )}
          </div>
        );
      })}

      <div className="sidebar-spacer" />

      <div className="sidebar-bottom">
        <nav className="sidebar-nav" aria-label="Account">
          {/* Billing hidden during pilot/demo phase
          <button
            className={`sidebar-link ${isActive('/billing') ? 'active' : ''}`}
            onClick={() => navigate('/billing')}
            aria-label="Billing"
            aria-current={isActive('/billing') ? 'page' : undefined}
          >
            <CreditCard aria-hidden="true" />
            {!collapsed && 'Billing'}
          </button>
          */}
          <button
            className={`sidebar-link ${isActive('/settings/tender-sources') ? 'active' : ''}`}
            onClick={() => navigate('/settings/tender-sources')}
            aria-label="Tender Sources"
            aria-current={isActive('/settings/tender-sources') ? 'page' : undefined}
          >
            <Globe aria-hidden="true" />
            {!collapsed && 'Tender Sources'}
          </button>
          <button
            className={`sidebar-link ${isActive('/settings/api') ? 'active' : ''}`}
            onClick={() => navigate('/settings/api')}
            aria-label="API & Webhooks"
            aria-current={isActive('/settings/api') ? 'page' : undefined}
          >
            <Key aria-hidden="true" />
            {!collapsed && 'API & Webhooks'}
          </button>
          <button
            className={`sidebar-link ${isActive('/integrations') ? 'active' : ''}`}
            onClick={() => navigate('/integrations')}
            aria-label="Integrations — connect Xero, SharePoint, HubSpot, Salesforce, DocuSign, Confluence, Mailgun, Projectworks"
            aria-current={isActive('/integrations') ? 'page' : undefined}
            title="Connect external tools (Xero, SharePoint, HubSpot, Salesforce, DocuSign, etc.)"
          >
            <Plug aria-hidden="true" />
            {!collapsed && 'Integrations'}
          </button>
          <button
            className={`sidebar-link ${isActive('/ai-usage') ? 'active' : ''}`}
            onClick={() => navigate('/ai-usage')}
            aria-label="AI usage — token spend, model breakdown, budget cap"
            aria-current={isActive('/ai-usage') ? 'page' : undefined}
            title="AI usage monitor — token spend by model, budget cap status"
          >
            <Activity aria-hidden="true" />
            {!collapsed && 'AI Usage'}
          </button>
          <button
            className={`sidebar-link ${isActive('/settings/branding') ? 'active' : ''}`}
            onClick={() => navigate('/settings/branding')}
            aria-label="Branding"
            aria-current={isActive('/settings/branding') ? 'page' : undefined}
          >
            <Palette aria-hidden="true" />
            {!collapsed && 'Branding'}
          </button>
          <button
            className={`sidebar-link ${isActive('/settings') && !location.pathname.startsWith('/settings/') ? 'active' : ''}`}
            onClick={() => navigate('/settings')}
            aria-label="Settings"
            aria-current={isActive('/settings') && !location.pathname.startsWith('/settings/') ? 'page' : undefined}
          >
            <Settings aria-hidden="true" />
            {!collapsed && 'Settings'}
          </button>
          <button
            className={`sidebar-link ${isActive('/help') ? 'active' : ''}`}
            onClick={() => navigate('/help')}
            aria-label="Help & docs"
            aria-current={isActive('/help') ? 'page' : undefined}
          >
            <HelpCircle aria-hidden="true" />
            {!collapsed && 'Help'}
          </button>
          <button
            className="sidebar-link"
            onClick={() => setDarkMode((prev) => !prev)}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
            {!collapsed && (darkMode ? 'Light Mode' : 'Dark Mode')}
          </button>
        </nav>
        {user && (
          <div style={{
            padding: collapsed ? '8px 4px' : '10px 12px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--primary)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
            }}>
              {user.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'}
            </div>
            {!collapsed && (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* F98 NAL pilot polish: title attr exposes the full name/email
                      on hover when it's been ellipsis-truncated. Without this
                      a long name is silently clipped with no recovery. */}
                  <div title={user.name || user.email} style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.name || user.email}
                  </div>
                  <div style={{ fontSize: '0.65rem', opacity: 0.7, textTransform: 'capitalize' }}>
                    {user.role || 'member'}
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 4, flexShrink: 0 }}
                  aria-label="Logout"
                  title="Logout"
                >
                  <LogOut size={16} />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
    {mobileOpen && <div className="sidebar-overlay active" onClick={() => setMobileOpen(false)} />}
    </>
  );
}
